import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

import { OpenFeatureProvider, useContextMutator } from '@openfeature/react-sdk';
import type { EvaluationContext } from '@openfeature/web-sdk';

import type { FeatureFlagUser } from './types';
import {
  ANONYMOUS_FEATURE_FLAG_CONTEXT,
  buildFeatureFlagContext,
} from './types';

const FEATURE_FLAG_LOG_PREFIX = '[feature-flags]';

type ContextUpdater =
  | EvaluationContext
  | ((currentContext: EvaluationContext) => EvaluationContext);

type FeatureFlagContextValue = {
  context: EvaluationContext;
  setApplicationContext: (nextContext: ContextUpdater) => void;
  resetApplicationContext: () => void;
};

const FeatureFlagContext = createContext<FeatureFlagContextValue>({
  context: ANONYMOUS_FEATURE_FLAG_CONTEXT,
  resetApplicationContext: () => undefined,
  setApplicationContext: () => undefined,
});

function mergeContexts(
  baseContext: EvaluationContext,
  applicationContext: EvaluationContext
) {
  return {
    ...baseContext,
    ...applicationContext,
    targetingKey:
      applicationContext.targetingKey ||
      baseContext.targetingKey ||
      'anonymous',
  };
}

function FeatureFlagContextBridge({
  children,
  initialApplicationContext,
  userData,
}: {
  children: ReactNode;
  initialApplicationContext?: EvaluationContext;
  userData?: FeatureFlagUser;
}) {
  const { setContext } = useContextMutator();
  const [applicationContext, setApplicationContextState] =
    useState<EvaluationContext>(initialApplicationContext ?? {});
  const [resolvedContext, setResolvedContext] = useState<EvaluationContext>(
    ANONYMOUS_FEATURE_FLAG_CONTEXT
  );
  const initialLastApplied =
    initialApplicationContext && Object.keys(initialApplicationContext).length
      ? JSON.stringify(mergeContexts(buildFeatureFlagContext(userData), initialApplicationContext))
      : JSON.stringify(ANONYMOUS_FEATURE_FLAG_CONTEXT);
  const lastAppliedContextRef = useRef<string>(initialLastApplied);
  const userContextSnapshot = useMemo(
    () => JSON.stringify(buildFeatureFlagContext(userData)),
    [userData]
  );

  useEffect(() => {
    const baseContext = JSON.parse(userContextSnapshot) as EvaluationContext;
    const nextContext = mergeContexts(baseContext, applicationContext);
    const nextSnapshot = JSON.stringify(nextContext);
    const logContext = nextContext as any;

    setResolvedContext((currentContext) => {
      if (JSON.stringify(currentContext) === nextSnapshot) {
        return currentContext;
      }

      return nextContext;
    });

    if (lastAppliedContextRef.current === nextSnapshot) {
      return;
    }

    // Defer the setContext call to allow child components (e.g. TenantAuthGuard)
    // to set applicationContext in their useEffect before we flush to OpenFeature.
    // The cleanup cancels a pending call if the context changes before it fires,
    // ensuring only a single call with the final context value.
    const timerId = setTimeout(() => {
      if (lastAppliedContextRef.current === nextSnapshot) {
        return;
      }

      lastAppliedContextRef.current = nextSnapshot;

      console.info(`${FEATURE_FLAG_LOG_PREFIX} applying context`, {
        isAnonymous: Boolean(logContext.isAnonymous),
        targetingKey: nextContext.targetingKey || 'anonymous',
        tenantId: logContext.tenantId || logContext.tenant_id || null,
        userId: logContext.userId || logContext.user_id || null,
      });

      void Promise.resolve(setContext(nextContext)).then(() => {
        console.info(`${FEATURE_FLAG_LOG_PREFIX} context applied`, {
          targetingKey: nextContext.targetingKey || 'anonymous',
        });
      });
    }, 0);

    return () => clearTimeout(timerId);
  }, [applicationContext, setContext, userContextSnapshot]);

  const setApplicationContext = useCallback((nextContext: ContextUpdater) => {
    setApplicationContextState((currentContext) => {
      if (typeof nextContext === 'function') {
        return nextContext(currentContext);
      }

      return nextContext;
    });
  }, []);

  const resetApplicationContext = useCallback(() => {
    setApplicationContextState({});
  }, []);

  const providerValue = useMemo(
    () => ({
      context: resolvedContext,
      resetApplicationContext,
      setApplicationContext,
    }),
    [resolvedContext, resetApplicationContext, setApplicationContext]
  );

  return (
    <FeatureFlagContext.Provider value={providerValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function FeatureFlagProvider({
  children,
  initialApplicationContext,
  userData,
}: {
  children: ReactNode;
  initialApplicationContext?: EvaluationContext;
  userData?: FeatureFlagUser;
}) {
  return (
    <OpenFeatureProvider>
      <FeatureFlagContextBridge
        initialApplicationContext={initialApplicationContext}
        userData={userData}
      >
        {children}
      </FeatureFlagContextBridge>
    </OpenFeatureProvider>
  );
}

export function useFeatureFlagContext() {
  return useContext(FeatureFlagContext);
}
